import React from 'react';
import {CircleLoading, Flexbox, Label} from "../../../../common/fusion-ui";
import {getAllocatedModuleName, isCurrUserRoleAdmin, isModuleAllocated} from "../../../utils/AppUtils";
import {ThemedButton, ThemedDropdownMenu, ThemedIconButton, ThemedInput} from "../../../index";
import {checkNullStr, getDefStr, getJsonValue, isBoolTrue, parseInteger} from "../../../../common/fusion-ui/utils/ReactUtils";
import {useTheme} from "../../../../common/ProjectThemeProvider";
import useScreenSize from "../../../../common/fusion-ui/hook/useScreenSize";
import {AnimationType, FramerAnimator} from "../../../../common/fusion-ui/animation/FramerAnimator";

/**
 * Create reusable heaer-bar with upload & recom-controls, search & sort features of internal pages.
 *
 * @example
 *  <InternalPageHeaderBar
 *      className={""}
 *      style={{}}
 *      bg={theme.primary10Opaque}
 *      pos={'sticky'}
 *
 *      moduleName={moduleName}
 *
 *      uploadUrl={GenRouteUrls.internal_uploadPage}
 *      recomUrl={GenRouteUrls.internal_recomPage}
 *
 *      showSearch={true}
 *      showSort={true}
 *      showSearchBy={true}
 *      searchByParams={SEARCH_BY_PARAMS}
 *      sortByParams={SORT_BY_PARAMS}
 *
 *      recordsLen={getArrLen(getRecordList())}
 *      filteredRecordsLen={getArrLen(getRecordList(true))}
 *      searchValue={getField(Fields.searchInputValue)}
 *      searchByDefOption={getField(Fields.searchBySelectedOption)}
 *      sortByDefOption={getField(Fields.sortBySelectedOption)}
 *      isSorting={sortListPending}
 *      isSearching={searchListPending}
 *      isSortAsc={isFieldTrue(Fields.isSortByOrderAsc)}
 *
 *      onUploadClick={(e) => {
 *      }}
 *      onRecomClick={(e) => {
 *      }}
 *      onSearchChange={(e, value) => handleSearchInputChange(value)}
 *      onSearchBy={(name, item, index) => {
 *          updateField(Fields.searchBySelectedOption, name);
 *          handleSearchInputChange(getField(Fields.searchInputValue), name);
 *      }}
 *      onSort={(name, item, index, sortKey) => {
 *          handleSortByChange(name, sortKey);
 *      }}
 * />
 */
const InternalPageHeaderBar = React.memo(({
                                              className = "",
                                              style = {},
                                              px = 3,
                                              py = 1,
                                              pb = 2,
                                              pt = 1,

                                              moduleName,

                                              bg,
                                              pos = 'sticky',
                                              justifyAt = 'start',

                                              uploadUrl,
                                              recomUrl,
                                              onUploadClick,
                                              onRecomClick,

                                              showSearch = true,
                                              showSearchBy = true,
                                              showSort = true,
                                              sortByParams = [],
                                              searchByParams = [],

                                              recordsLen,
                                              filteredRecordsLen,
                                              searchValue,
                                              searchByDefOption,
                                              sortByDefOption,
                                              isSorting,
                                              isSearching,
                                              isSortAsc,

                                              onSearchChange,
                                              onSearchBy,
                                              onSort,

                                              leftComponent,
                                              rightComponent,
                                              leftControls,
                                              midControls,
                                              rightControls,
                                          }) => {
    const TAG = "InternalPageHeaderBar";

    const theme = useTheme();
    const {isScreenUpSm} = useScreenSize();

    const animator = new FramerAnimator();

    const SEARCH_INPUT_ID = `${TAG}_SEARCH_INPUT_BOX`;

    return (<>
        {/*{createHeader()}*/}
        {animator.setAnimationType(AnimationType.SLIDE_LEFT)
            // .setDuration(500)
            .render(createHeader())}
    </>)

    function createHeader() {
        return <div
            className={`w-100 m-0 p-0 px-${px} py-${py} pb-${pb} pt-${pt} jc-${justifyAt} d-flex jc-start-sm al-center al-start-sm flex-wrap  fd-row position-${pos} ${className}`}
            style={{
                top: '.01rem',
                left: '0rem',
                zIndex: 1,
                backgroundColor: isScreenUpSm ? 'transparent' : getDefStr(bg, theme.primary10Opaque),
                ...style
            }}>

            {/*left component*/}
            {leftComponent ? <div className={'px-1x'}>
                {leftComponent}
            </div> : <span></span>}

            {/*left - controls*/}
            <Flexbox className={`m-0x p-0 ${isScreenUpSm ? 'mt-3x' : ''}`} justifyAt={'start'} alignAt={'center'} style={{margin: '.2rem 0 0 0'}}>
                {leftControls && leftControls}

                {/*upload-btn*/}
                {(isCurrUserRoleAdmin() && checkNullStr(uploadUrl) && isModuleAllocated(moduleName)) && <ThemedIconButton
                    className={'fs-sm fw-semi-bold text-nowrap'}
                    outlined={true}
                    height={'28px'}
                    size={'28px'}
                    m={1}
                    ml={leftControls ? 1 : 0}
                    tooltip={`Upload ${getAllocatedModuleName(moduleName)}`}
                    icon={'bi bi-upload'}
                    iconSize={'15px'}
                    href={uploadUrl}
                    variant={theme.primaryBlue}
                    onClick={onUploadClick}
                />}

                {midControls && midControls}

                {/*recom-btn*/}
                {(checkNullStr(recomUrl) && isModuleAllocated(moduleName)) && <ThemedButton
                    className={'fs-sm fw-semi-bold text-nowrap'}
                    outlined={true}
                    sleek={true}
                    mx={(isCurrUserRoleAdmin() && checkNullStr(uploadUrl)) ? 2 : 0}
                    m={isScreenUpSm && 1}
                    height={'28px'}
                    text={`Recommend ${getRecomBtnText()}`}
                    href={recomUrl}
                    variant={theme.primaryBlue}
                    onClick={onRecomClick}
                />}

                {rightControls && rightControls}
            </Flexbox>

            {/*right*/}
            <div className={'m-0x p-0 d-flex fd-row flex-wrap jc-start al-center al-start-sm'}>
                {/*mid - search*/}
                {isBoolTrue(showSearch) &&
                    <div
                        className={`m-0 p- position-relative ${parseInteger(recordsLen) <= 0 ? 'disable-item' : ''} ${isScreenUpSm ? `m-1` : (((isCurrUserRoleAdmin() && checkNullStr(uploadUrl) || checkNullStr(recomUrl)) ? `ms-2` : 'ms-n0'))}`}>
                        {/*search-input*/}
                        <ThemedInput
                            mainWrapperClassName={`w-100`}
                            className={`w-100 rounded-10 pe-11`}
                            style={{height: '28px', width: '270px', backgroundColor: theme.bg}}
                            placeholder={`Search... `}
                            value={searchValue}
                            showMsg={false}
                            id={SEARCH_INPUT_ID}
                            onChange={(e) => {
                                if (onSearchChange) onSearchChange(e, e.target.value);
                            }}
                        />

                        {/*end - loader & search-by dropdown*/}
                        <Flexbox className={''} justifyAt={'end'} alignAt={'center'} position={'absolute'}
                                 style={{top: '.38rem', right: '.25rem'}}>
                            {/*loader*/}
                            {isBoolTrue(isSearching) && <CircleLoading widthClass={'x'} size={'14px'} borderWidth={'2px'}/>}

                            {/*search-result count*/}
                            {checkNullStr(searchValue) && <Label
                                className={`m-0 p-0 px-2 mx-1 text-nowrap fs-sm rounded-10 select-none fw-semi-bold d-flex al-center flex-nowrap jc-center`}
                                tooltip={'Search Results'}
                                color={theme.text}
                                bgColor={theme.text10}
                                height={'20px'}>
                                {filteredRecordsLen}
                            </Label>}
                            {/*shortcut*/}
                            {/*{(!isSearchOngoing() && checkNullArr(getRecordList())) &&*/}
                            {/*    <Label*/}
                            {/*        className={`m-0 p-0 px-2 mx-1 text-nowrap fs-sm select-none fw-semi-bold d-flex al-center flex-nowrap jc-center`}*/}
                            {/*        color={theme.text}*/}
                            {/*        bgColor={theme.text10}*/}
                            {/*        height={'20px'}>*/}
                            {/*        {InputFocusShortcutKey}*/}
                            {/*    </Label>}*/}

                            {/*search-by dropdown*/}
                            {isBoolTrue(showSearchBy) && <ThemedDropdownMenu
                                className={''}
                                buttonClassName={'border-none fw-semi-bold rounded-10 px-1 text-nowrap'}
                                buttonHeight={'22px'}
                                outlined={false}
                                buttonStyle={{
                                    // top: '.38rem', right: '.25rem',
                                }}
                                textPrefix={<span className={'fw-bold'}>With:</span>}
                                defOption={searchByDefOption}
                                onItemClick={(name, index, item) => {
                                    if (onSearchBy) onSearchBy(name, item, index);
                                }}>{Object.values(searchByParams)}</ThemedDropdownMenu>}
                        </Flexbox>
                    </div>}

                {/*right - sort-by dropdown*/}
                {isBoolTrue(showSort) && <Flexbox className={`${isScreenUpSm ? `m-1 ms-n1x` : `ms-2`}`}
                                                  justifyAt={'end'} alignAt={'center'} disabled={parseInteger(recordsLen) <= 0}>
                    <ThemedDropdownMenu
                        className={'mb-n1'}
                        buttonStyle={{marginTop: '.2rem'}}
                        buttonClassName={'border-none fw-semi-bold  text-nowrap'}
                        textWrap={'nowrap'}
                        buttonHeight={'28px'}
                        // variant={'primaryBlue'}
                        outlined={false}
                        textPrefix={<span className={'fw-bold'}>Sort by:</span>}
                        textPostfix={<i className={`${!isBoolTrue(isSortAsc) ? 'bi bi-sort-up-alt' : 'bi bi-sort-down'}`}/>}
                        defOption={sortByDefOption}
                        onItemClick={(name, index, item) => {
                            if (onSort) onSort(name, item, index, getJsonValue(item, 'sortKey'))
                        }}>
                        {Object.values(sortByParams).map((item) => {
                            return {
                                ...item,
                                // iconRight: isBoolTrue(isSortAsc) ? item.iconRight : item.iconRightReverse
                            }
                        })}
                    </ThemedDropdownMenu>

                    {/*loader*/}
                    {(isSorting) && <CircleLoading widthClass={'x'} wrapperClassName={'ms-1'} size={'14px'} borderWidth={'2px'}/>}
                </Flexbox>}
            </div>

            {rightComponent && <div className={`${isScreenUpSm ? `m-1` : `ms-2`}`}>
                {rightComponent}
            </div>}
        </div>
    }

    function getRecomBtnText() {
        let text = getAllocatedModuleName(moduleName) + '';
        if (text.toLowerCase().endsWith('s')) {
            text = text.substring(0, text.length - 1);
        }
        return text;
    }
});
export default InternalPageHeaderBar;
